import React from 'react';

import PropTypes from 'prop-types';
import { WeezHeader, Message, Input, RadioLabel, TooltipHelp, Card, Form, Select, ColorPicker, SplitContainer } from '@weezevent/nacre';

import { useTranslation } from 'react-i18next';
import QRCodePreview from './qrCodePreview';
import CustomMessage from './customMessage';
import Minisite from '../../../models/minisite';
import { useForm } from '../../../utils/validators';

import Style from './qr-code.module.css';
import StyleMiniSite from '../../../styles/minisite/minisite.module.css';

import { LANGUAGES } from '../../../components/translations/translationModal';

function RedirectionPageForm({ event, organization, page, setPage }) {
    const { t } = useTranslation();

    const [minisite, isLoadingMinisite] = Minisite.useApiModel(
        {
            cache: false,
            organizationId: organization.id,
            id: event?.site,
            launch: Boolean(event?.site)
        },
        [event, organization]
    );

    const [customWebsiteUrl, setCustomWebsiteUrl] = React.useState(event?.website);

    const onChangeWebsiteUrl = React.useCallback(newUrl => {
        setCustomWebsiteUrl(newUrl);
        setPage({ key: 'website', url: newUrl });
    });

    const widgetUrl = React.useMemo(() => {
        return `${window.weezConfig.api.widgetUrl}/ticket/E${event.id}/?code=${event.default_widget_access_code}`;
    }, [event]);

    const pageTypes = React.useMemo(() => {
        return {
            minisite: { key: 'minisite', url: event?.gemino_url },
            widget: { key: 'widget', url: widgetUrl },
            website: { key: 'website', url: customWebsiteUrl }
        };
    }, [event, customWebsiteUrl, t]);

    return (
        <div>
            <Form.Field>
                <Form.Label label={t('ticket.qrcode.form.redirectionPage.label')} />
                <div className={Style['radio-button-grid']}>
                    {Object.keys(pageTypes).map(key => (
                        <RadioLabel
                            // className={StyleRate['radio-label-custom']}
                            disabled={pageTypes[key].disabled}
                            key={key}
                            inline
                            bordered
                            name={key}
                            checked={page.key === key}
                            label={
                                <>
                                    {t(`ticket.qrcode.form.redirectionPage.${key}.label`)}{' '}
                                    <TooltipHelp title={t(`ticket.qrcode.form.redirectionPage.tooltip`, { page: t(`ticket.qrcode.form.redirectionPage.${key}.tooltip`) })} />
                                </>
                            }
                            onClick={() => setPage(pageTypes[key])}
                            value={key}
                        />
                    ))}
                </div>
                {page.key === 'minisite' && !minisite?.is_published && !isLoadingMinisite && (
                    <Form.Field>
                        <Message type="warning" title={t(`ticket.qrcode.form.warningMinisite.title`)}>
                            {t(`ticket.qrcode.form.warningMinisite.message`)}
                        </Message>
                    </Form.Field>
                )}
                {page.key === 'website' && (
                    <Form.Field>
                        <Form.Label label={t(`ticket.qrcode.form.redirectionPage.website.label`)} />
                        <Input value={customWebsiteUrl} onChange={(synth, { value }) => onChangeWebsiteUrl(value)} />
                    </Form.Field>
                )}
            </Form.Field>
        </div>
    );
}

function MainColorPicker({ color, setColor }) {
    const { t } = useTranslation();

    return (
        <Form.Field style={{ display: 'flex', 'align-items': 'center', 'margin-top': '10px' }}>
            <ColorPicker value={color} onChange={(synth, value) => setColor(value)} />
            <span style={{ marginLeft: '6px' }}>{t('ticket.sell_on_my_site.card_module.primaryColor')}</span>
        </Form.Field>
    );
}

function DefaultLanguageSelector({ event, locale, setLocale }) {
    const { t } = useTranslation();

    const languageOptions = React.useMemo(() => {
        return event.languages.map(lang => {
            if (lang === 'it') {
                lang = 'it-it';
            }
            return {
                text: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [event]);

    return (
        <Form.Field>
            <Form.Label label={t('ticket.chart.defaultLanguage')} />
            <Select className={StyleMiniSite['select-language']} value={locale} options={languageOptions} onChange={value => setLocale(value)} />
        </Form.Field>
    );
}

function OriginInput({ placeholder, origin, setOrigin }) {
    const { t } = useTranslation();

    return (
        <Form.Field>
            <Form.Label label={t('ticket.qrcode.form.trackingOrigin.label')} />
            <Input placeholder={placeholder} value={origin} onChange={(synth, { value }) => setOrigin(value)} />
        </Form.Field>
    );
}

export default function QRCodeShare({ event, organization }) {
    const { t } = useTranslation();

    const defaultForm = React.useMemo(() => {
        return { color: '#0032FA', locale: event.default_language, origin: 'qr-code' };
    }, [event]);

    const [form, setForm] = useForm(defaultForm);

    const [page, setPage] = React.useState({ key: 'minisite', url: event.gemino_url });

    return (
        <div>
            <WeezHeader title={t('ticket.qrcode.pageTitle')}>{t('ticket.qrcode.pageSubtitle')}</WeezHeader>
            <CustomMessage title={t('ticket.qrcode.pageTitle')} icon="qr">
                {t('ticket.qrcode.message.description')}
            </CustomMessage>
            <SplitContainer
                size="maxi"
                additional={<QRCodePreview color={form.color} locale={form.locale} origin={form.origin || defaultForm.origin} baseUrl={page.url} pageType={page.key} />}
            >
                <Card>
                    <Card.Header>
                        <Card.Title title={t('ticket.qrcode.form.title')} />
                    </Card.Header>
                    <Card.Content>
                        <RedirectionPageForm event={event} organization={organization} page={page} setPage={setPage} />
                        {page.key === 'widget' && <MainColorPicker color={form.color} setColor={value => setForm('color', value)} />}
                        <DefaultLanguageSelector event={event} locale={form.locale} setLocale={value => setForm('locale', value)} />
                        <OriginInput placeholder={defaultForm.origin} origin={form.origin} setOrigin={value => setForm('origin', value)} />
                    </Card.Content>
                </Card>
            </SplitContainer>
        </div>
    );
}

QRCodeShare.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};
